import React, { useState } from "react";
import useMasterUsers from "./useMasterUser";
import EditImg from "../../images/SideBar/Edit";
import Loader from "../../components/Loader";
import CommonDataGrid from "../../components/CommonDataGrid";
import Button from "../../components/Botton";
import { Toaster } from "react-hot-toast";
import { GridColDef } from "@mui/x-data-grid";
import { TextField } from "@mui/material";
import DeleteImg from "../../images/SideBar/Delete";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import EditMasterUserIndex from "./EditMasterUser/EditMasterUser";
import AddUserModal from "./AddMasterUser/AddMasterUser";
import AdminPermissionManager from "./AdminPermissionManager";
import { DisabledByDefault, Security } from "@mui/icons-material";

const MasterUsers: React.FC = () => {
  const {
    selectedUserId,
    usersData,
    loading,
    searchQuery,
    setSearchQuery,
    handleEdit,
    handleDelete,
    handleStatusUpdate,
    handleDateFilter,
  } = useMasterUsers();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false);
  const [selectedUserForPermissions, setSelectedUserForPermissions] = useState<any>(null);

  const openDeleteModal = (id: string) => {
    setUserToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (userToDelete) {
      handleDelete(userToDelete);
      setIsDeleteModalOpen(false);
    }
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setUserToDelete(null);
  };

  const openAddModal = () => {
    setIsAddModalOpen(true);
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
  };

  const openPermissionModal = (user: any) => {
    if (user.role === 'SUPER_ADMIN') {
      return; // Super admins don't need permission assignment
    }
    setSelectedUserForPermissions(user);
    setIsPermissionModalOpen(true);
  };

  const closePermissionModal = () => {
    setIsPermissionModalOpen(false);
    setSelectedUserForPermissions(null);
  };

  const handleStatusToggle = (id: string, currentIsDisabled: boolean) => {
    // Toggle the disabled status
    handleStatusUpdate(id, !currentIsDisabled);
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      align: "center",
      headerAlign: "center",
      flex: 0.6,
      renderCell: (params) => (
        <div className="flex items-center justify-center mt-3">
          <div className="text-center">
            <div className="font-medium">{params.row.name || "N/A"}</div>
            {params.row.isDemo && (
              <span className="text-xs bg-yellow-100 text-yellow-800 px-1 rounded">
                Demo
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      align: "center",
      headerAlign: "center",
      flex: 0.8,
      renderCell: (params) => (
        <div className="mt-3 text-center">
          {params.row.email || "N/A"}
        </div>
      ),
    },
    {
      field: "mobileNo",
      headerName: "Phone Number",
      align: "center",
      headerAlign: "center",
      flex: 0.6,
      renderCell: (params) => (
        <div className="mt-3 text-center">
          {params.row.mobileNo || "N/A"}
        </div>
      ),
    },
    {
      field: "role",
      headerName: "Role",
      align: "center",
      headerAlign: "center",
      flex: 0.4,
      renderCell: (params) => (
        <div className="flex justify-center mt-3">
          <span className={`px-2 py-1 rounded text-xs font-medium ${params.row.role === 'ADMIN'
              ? 'bg-purple-100 text-purple-800'
              : 'bg-blue-100 text-blue-800'
            }`}>
            {params.row.role}
          </span>
        </div>
      ),
    },
    // {
    //   field: "standard",
    //   headerName: "Education",
    //   align: "center",
    //   headerAlign: "center",
    //   flex: 0.4,
    //   renderCell: (params) => (
    //     <div className="mt-3 text-center">
    //       {params.row.standard || "N/A"}
    //     </div>
    //   ),
    // },
    // {
    //   field: "courseName",
    //   headerName: "Course",
    //   align: "center",
    //   headerAlign: "center",
    //   flex: 0.5,
    //   renderCell: (params) => (
    //     <div className="mt-3 text-center">
    //       {params.row.courseName || "N/A"}
    //     </div>
    //   ),
    // },
    {
      field: "isDisabled",
      headerName: "Status",
      align: "center",
      headerAlign: "center",
      flex: 0.4,
      renderCell: (params) => (
        <div className="flex justify-center mt-3">
          <button
            onClick={() => handleStatusToggle(params.row._id, params.row.isDisabled)}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${!params.row.isDisabled
                ? "bg-green-100 text-green-800 hover:bg-green-200"
                : "bg-red-100 text-red-800 hover:bg-red-200"
              }`}
          >
            {!params.row.isDisabled ? "Active" : "Disabled"}
          </button>
        </div>
      ),
    },

    {
      field: "createdAt",
      headerName: "Joined Date",
      align: "center",
      headerAlign: "center",
      flex: 0.5,
      renderCell: (params) => (
        <div className="mt-3 text-center">
          {new Date(params.row.createdAt).toLocaleDateString()}
        </div>
      ),
    },
    {
      field: "Actions",
      headerName: "Actions",
      align: "center",
      headerAlign: "center",
      flex: 0.6,
      renderCell: (params) => (
        <div className="flex h-full items-center justify-center gap-2">
          <button
            onClick={() => handleEdit(params.row._id)}
            className="text-fontColor hover:text-primary-2 p-1"
            title="Edit User"
          >
            <EditImg />
          </button>
          {params.row.role !== 'SUPER_ADMIN' && (
            <button
              onClick={() => openPermissionModal(params.row)}
              className="text-blue-600 hover:text-blue-800 p-1"
              title="Manage Permissions"
            >
              <Security />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="mx-auto p-4">
      {loading && <Loader />}
      {selectedUserId ? (
        <EditMasterUserIndex userId={selectedUserId} />
      ) : (
        <div className="space-y-4 mt-4">
          {/* Header */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Master User Management</h1>
              <p className="text-gray-600">Manage administrator accounts and their permissions</p>
            </div>

            <div className="flex justify-between items-center mb-4">
              <div className="flex space-x-6">
                {/* Summary Stats */}
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">
                    Total Users: <span className="font-semibold text-gray-900">{usersData.length}</span>
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">
                    Active: <span className="font-semibold text-gray-900">{usersData.filter((u: any) => !u.isDisabled).length}</span>
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">
                    Admins: <span className="font-semibold text-gray-900">{usersData.filter((u: any) => u.role === 'ADMIN').length}</span>
                  </span>
                </div>
              </div>
              <Button
                onClick={openAddModal}
                className="py-2 px-6 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium"
                children="Add New User"
                type="button"
              />
            </div>

            {/* Search Bar */}
            <div className="mb-4">
              <TextField
                fullWidth
                variant="outlined"
                label="Search"
                placeholder="Type to search master users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                sx={{ maxWidth: '500px' }}
              />
            </div>

            <CommonDataGrid
              id="masterUser"
              rows={usersData}
              columns={columns}
              loading={!usersData}
              showConfirmation={false}
              confirmationMessage={""}
              onCancelDelete={function (): void {
                throw new Error("Function not implemented.");
              }}
              onConfirmDelete={function (): void {
                throw new Error("Function not implemented.");
              }}
              toolbarProps={{ toolbar: { showQuickFilter: true } }}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        message="Are you sure you want to disable this user? They won't be able to access this platform"
      />

      {/* Add User Modal */}
      <AddUserModal
        isOpen={isAddModalOpen}
        onClose={closeAddModal}
      />

      {/* Permission Management Modal */}
      {selectedUserForPermissions && (
        <AdminPermissionManager
          isOpen={isPermissionModalOpen}
          onClose={closePermissionModal}
          admin={selectedUserForPermissions}
        />
      )}

      <Toaster />
    </div>
  );
};

export default MasterUsers;